// @ts-nocheck
import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertTriangle,
  Clock,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  PartyPopper,
} from 'lucide-react';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

const formatRole = (role: string) => {
  const map: Record<string, string> = {
    team_owner: 'Team Owner',
    team_billing_contact: 'Billing Contact',
    team_manager: 'Team Manager',
    head_coach: 'Head Coach',
    assistant_coach: 'Assistant Coach',
    viewer: 'Viewer',
  };
  return map[role] ?? role;
};

const passwordStrength = (pw: string): { label: string; color: string; pct: number } => {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 2) return { label: 'Weak', color: 'bg-destructive', pct: 33 };
  if (score <= 3) return { label: 'Medium', color: 'bg-amber-500', pct: 66 };
  return { label: 'Strong', color: 'bg-green-600', pct: 100 };
};

const signupSchema = z
  .object({
    password: z.string().min(8, 'At least 8 characters'),
    confirmPassword: z.string(),
    fullName: z.string().max(100).optional(),
    agree: z.literal(true, { errorMap: () => ({ message: 'You must agree to continue' }) }),
  })
  .refine((d) => d.password === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords must match',
  });

type SignupValues = z.infer<typeof signupSchema>;

export default function AcceptTeamInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [accepted, setAccepted] = useState<{ team: any } | null>(null);
  const [showSignup, setShowSignup] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);

  const { data: preview, isLoading, error } = useQuery({
    queryKey: ['invite-preview', token],
    queryFn: async () => {
      const { data, error: fnErr } = await invokeEdgeFunction(
        'rle-accept-team-invite',
        { body: { token, preview: true } }
      );
      if (fnErr) throw fnErr;
      if (!data?.ok) {
        const err = new Error(data?.error?.message ?? 'Invalid invitation');
        (err as any).code = data?.error?.code ?? 'TOKEN_INVALID';
        (err as any).expires_at = data?.error?.context?.expires_at;
        throw err;
      }
      return data.data;
    },
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const { data, error: fnErr } = await invokeEdgeFunction(
        'rle-accept-team-invite',
        { body: { token } }
      );
      if (fnErr) throw fnErr;
      if (!data?.ok) throw new Error(data?.error?.message ?? 'Failed to accept');
      return data.data;
    },
    onSuccess: (data) => {
      setAccepted({ team: data.team });
      setTimeout(() => {
        toast.success(`You're now a member of ${data.team?.display_name ?? 'the team'}`);
        navigate(data.redirect_path ?? '/');
      }, 1500);
    },
  });

  const signupForm = useForm<SignupValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: { password: '', confirmPassword: '', fullName: '', agree: undefined as any },
  });

  const pwValue = signupForm.watch('password');
  const strength = useMemo(() => passwordStrength(pwValue || ''), [pwValue]);

  const handleSignup = async (values: SignupValues) => {
    setSignupError(null);
    try {
      const { data, error: fnErr } = await invokeEdgeFunction(
        'rle-accept-team-invite',
        {
          body: {
            token,
            auth_action: 'create_account',
            password: values.password,
            full_name: values.fullName || null,
          },
        }
      );
      if (fnErr) throw fnErr;
      if (!data?.ok) throw new Error(data?.error?.message ?? 'Failed to create account');

      const result = data.data;
      if (result.session?.access_token && result.session?.refresh_token) {
        await supabase.auth.setSession({
          access_token: result.session.access_token,
          refresh_token: result.session.refresh_token,
        });
      } else {
        await supabase.auth.signInWithPassword({
          email: preview.invitee_email,
          password: values.password,
        });
      }

      toast.success('Welcome to RLevents!');
      navigate(result.redirect_path ?? '/');
    } catch (e: any) {
      setSignupError(e.message ?? 'Something went wrong');
    }
  };

  const Wrapper = ({ children }: { children: React.ReactNode }) => (
    <div className="min-h-screen bg-background flex items-start justify-center px-4 py-12 sm:py-20">
      <div className="w-full max-w-[480px] animate-in fade-in duration-500">
        <div className="text-center mb-6">
          <span className="text-xl font-bold text-secondary tracking-tight">RLevents</span>
        </div>
        {children}
      </div>
    </div>
  );

  if (isLoading || authLoading) {
    return (
      <Wrapper>
        <Card>
          <CardContent className="p-8 space-y-4">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Loading invitation...</span>
            </div>
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </Wrapper>
    );
  }

  if (error) {
    const code = (error as any).code as string;
    const expiresAt = (error as any).expires_at;

    let icon = <AlertTriangle className="h-12 w-12 text-destructive" />;
    let title = 'Invalid invitation link';
    let body =
      'This invitation link is not valid. Please check the link in your email, or ask the person who invited you to send a new one.';
    let btnLabel = 'Go to RLevents home';
    let btnAction = () => navigate('/');

    if (code === 'TOKEN_EXPIRED') {
      icon = <Clock className="h-12 w-12 text-destructive" />;
      title = 'Invitation expired';
      const dateStr = expiresAt ? new Date(expiresAt).toLocaleDateString() : 'an earlier date';
      body = `This invitation expired on ${dateStr}. Ask the person who invited you to send a new invitation.`;
    } else if (code === 'TOKEN_ALREADY_USED') {
      icon = <CheckCircle2 className="h-12 w-12 text-green-600" />;
      title = 'Invitation already accepted';
      body =
        'This invitation has already been used. If that was you, sign in to access the team.';
      btnLabel = 'Sign in';
      btnAction = () => navigate('/login');
    }

    return (
      <Wrapper>
        <Card>
          <CardContent className="p-8 text-center space-y-4">
            <div className="flex justify-center">{icon}</div>
            <h1 className="text-xl font-semibold">{title}</h1>
            <p className="text-sm text-muted-foreground">{body}</p>
            <Button onClick={btnAction} className="w-full">
              {btnLabel}
            </Button>
          </CardContent>
        </Card>
      </Wrapper>
    );
  }

  if (!preview) return null;

  if (accepted) {
    return (
      <Wrapper>
        <Card>
          <CardContent className="p-8 text-center space-y-4">
            <PartyPopper className="h-12 w-12 text-secondary mx-auto" />
            <h1 className="text-2xl font-bold">
              🎉 Welcome to {accepted.team?.display_name}!
            </h1>
            <p className="text-sm text-muted-foreground">Taking you to your team...</p>
          </CardContent>
        </Card>
      </Wrapper>
    );
  }

  const team = preview.team ?? {};
  const inviteeEmail: string = preview.invitee_email ?? '';
  const inviteeName: string = preview.invitee_name ?? '';
  const inviter = preview.inviter ?? {};

  const emailMatches =
    user?.email && user.email.toLowerCase() === inviteeEmail.toLowerCase();

  return (
    <Wrapper>
      <Card>
        <CardContent className="p-6 sm:p-8 space-y-5">
          <div>
            <h1 className="text-[22px] font-bold leading-tight">
              🏀 You're invited to {team.display_name}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Role: {formatRole(preview.role)}
              {inviter.name ? ` · Invited by ${inviter.name}` : ''}
            </p>
          </div>

          <div className="bg-muted/50 rounded-lg p-4 flex items-center gap-3">
            {team.logo_url ? (
              <img
                src={team.logo_url}
                alt={team.display_name}
                className="h-12 w-12 rounded-md object-cover"
              />
            ) : (
              <div className="h-12 w-12 rounded-md bg-secondary/20 flex items-center justify-center text-secondary font-bold">
                {team.display_name?.[0]?.toUpperCase() ?? '?'}
              </div>
            )}
            <div>
              <div className="font-semibold text-sm">{team.display_name}</div>
              {(team.age_group || team.gender) && (
                <div className="text-xs text-muted-foreground">
                  {[team.age_group, team.gender].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          </div>

          {user && emailMatches && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Signed in as {user.email}</p>

              {acceptMutation.isError && (
                <Alert variant="destructive">
                  <AlertDescription className="flex items-center justify-between gap-2">
                    <span>
                      Something went wrong:{' '}
                      {(acceptMutation.error as Error)?.message ?? 'Unknown error'}.
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => acceptMutation.mutate()}
                    >
                      Try again
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              <Button
                size="lg"
                className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                disabled={acceptMutation.isPending}
                onClick={() => acceptMutation.mutate()}
              >
                {acceptMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Accepting...
                  </>
                ) : (
                  'Accept invitation'
                )}
              </Button>
            </div>
          )}

          {user && !emailMatches && (
            <div className="space-y-3">
              <Alert variant="destructive">
                <AlertTitle>Wrong account</AlertTitle>
                <AlertDescription>
                  This invitation was sent to <strong>{inviteeEmail}</strong> but you're
                  signed in as <strong>{user.email}</strong>. Sign out first and then sign
                  in with the invited email, or create a new account for {inviteeEmail}.
                </AlertDescription>
              </Alert>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={async () => {
                    await supabase.auth.signOut();
                  }}
                >
                  Sign out
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate('/')}
                >
                  Go home
                </Button>
              </div>
            </div>
          )}

          {!user && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Card className="border">
                <CardContent className="p-4 space-y-2">
                  <div className="font-semibold text-sm">I already have an account</div>
                  <p className="text-xs text-muted-foreground">
                    Sign in with {inviteeEmail}
                  </p>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() =>
                      navigate(
                        `/login?redirect=/invites/team/${token}&email=${encodeURIComponent(inviteeEmail)}`
                      )
                    }
                  >
                    Sign in
                  </Button>
                </CardContent>
              </Card>

              <Card className="border">
                <CardContent className="p-4 space-y-2">
                  <div className="font-semibold text-sm">I'm new here</div>
                  <p className="text-xs text-muted-foreground">Create your account</p>
                  {!showSignup ? (
                    <Button className="w-full" onClick={() => setShowSignup(true)}>
                      Create account
                    </Button>
                  ) : (
                    <form
                      onSubmit={signupForm.handleSubmit(handleSignup)}
                      className="space-y-3 pt-2"
                    >
                      <div>
                        <Label className="text-xs">Email</Label>
                        <Input value={inviteeEmail} readOnly disabled />
                      </div>
                      <div>
                        <Label className="text-xs">Full name</Label>
                        <Input
                          defaultValue={inviteeName}
                          {...signupForm.register('fullName')}
                          placeholder="Optional"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Password</Label>
                        <div className="relative">
                          <Input
                            type={showPw ? 'text' : 'password'}
                            {...signupForm.register('password')}
                          />
                          <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                            onClick={() => setShowPw((v) => !v)}
                          >
                            {showPw ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                        {pwValue && (
                          <div className="mt-1.5 flex items-center gap-2">
                            <div className="h-1.5 flex-1 bg-muted rounded overflow-hidden">
                              <div
                                className={`h-full transition-all ${strength.color}`}
                                style={{ width: `${strength.pct}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {strength.label}
                            </span>
                          </div>
                        )}
                        {signupForm.formState.errors.password && (
                          <p className="text-xs text-destructive mt-1">
                            {signupForm.formState.errors.password.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs">Confirm password</Label>
                        <Input
                          type={showPw ? 'text' : 'password'}
                          {...signupForm.register('confirmPassword')}
                        />
                        {signupForm.formState.errors.confirmPassword && (
                          <p className="text-xs text-destructive mt-1">
                            {signupForm.formState.errors.confirmPassword.message}
                          </p>
                        )}
                      </div>
                      <div className="flex items-start gap-2">
                        <Checkbox
                          id="agree"
                          onCheckedChange={(c) =>
                            signupForm.setValue('agree', c === true ? true : (undefined as any), {
                              shouldValidate: true,
                            })
                          }
                        />
                        <Label htmlFor="agree" className="text-xs leading-tight">
                          I agree to the Terms of Service and Privacy Policy
                        </Label>
                      </div>
                      {signupForm.formState.errors.agree && (
                        <p className="text-xs text-destructive">
                          {signupForm.formState.errors.agree.message}
                        </p>
                      )}
                      {signupError && (
                        <Alert variant="destructive">
                          <AlertDescription className="text-xs">
                            {signupError}
                          </AlertDescription>
                        </Alert>
                      )}
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={signupForm.formState.isSubmitting}
                      >
                        {signupForm.formState.isSubmitting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" /> Creating...
                          </>
                        ) : (
                          'Create account & accept invitation'
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>
    </Wrapper>
  );
}